import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { signup, verifySignupOTP, resendSignupOtp } from "../api/apis";
import { User, Mail, Lock, ArrowRight, Eye, EyeOff, LogOut, Menu, X } from 'lucide-react';
import logo from '../assets/Images/logo.png';
import OtpEnter from '../Pages/OtpEnter';

// Create fallback localStorage functions
const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return defaultValue;
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  } catch (error) {
    return defaultValue;
  }
};

const removeFromLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';
  const searchParams = new URLSearchParams(location.search);
  const nextParam = searchParams.get('next');
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupUsername, setSignupUsername] = useState('');

  const handleLogout = () => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  };

  useEffect(() => {
    // intentionally left blank to avoid bouncing from auth pages
  }, []);

  const [formData, setFormData] = useState({
    username: "",
    emailId: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username) newErrors.username = "Username is required.";
    if (!formData.emailId) newErrors.emailId = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.emailId))
      newErrors.emailId = "Enter a valid email.";
    if (!formData.password) newErrors.password = "Password is required.";
    if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters.";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Confirm your password.";
    else if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setLoader(true);
      const response = await signup({
        username: formData.username,
        emailId: formData.emailId,
        password: formData.password,
      });

      if (response.data.signUp === true) {
        // Store email and username for OTP verification
        setSignupEmail(formData.emailId);
        setSignupUsername(formData.username);

        // Show OTP verification screen
        setShowOtpScreen(true);
      } else {
        const errorMessage = response.data.error;
        toast.error(errorMessage || "Sign up failed.");
      }
      setLoader(false);
    } catch (error) {
      toast.error(error.message || "An unexpected error occurred.");
      setLoader(false);
    }
  };

  const handleOtpVerification = async (otp) => {
    try {
      const response = await verifySignupOTP({
        email: signupEmail,
        otp: otp
      });

      if (response.data.success === true) {
        toast.success("Account verified successfully! You are now logged in.");
        // The user is automatically verified and logged in via the backend
        // Determine best redirect target after signup
        let target = from;
        try {
          const ss = sessionStorage.getItem('postLoginRedirect');
          if (ss && typeof ss === 'string') {
            target = ss;
            sessionStorage.removeItem('postLoginRedirect');
          }
        } catch {}
        if (!target && nextParam) {
          target = nextParam;
        }
        // Navigate back to intended page if present
        navigate(target, { replace: true });
        return response;
      } else {
        toast.error(response.data.message || "Invalid OTP");
        throw new Error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error(error.message || "OTP verification failed.");
      throw error;
    }
  };

  const handleOtpSuccess = () => {
    setShowOtpScreen(false);
    // User is already logged in and verified via handleOtpVerification
    let target = from;
    try {
      const ss = sessionStorage.getItem('postLoginRedirect');
      if (ss && typeof ss === 'string') {
        target = ss;
        sessionStorage.removeItem('postLoginRedirect');
      }
    } catch {}
    if (!target && nextParam) {
      target = nextParam;
    }
    navigate(target, { replace: true });
  };

  const handleResendOtp = async () => {
    try {
      if (!signupEmail) {
        toast.error("Missing email to resend OTP.");
        return;
      }
      const res = await resendSignupOtp({ email: signupEmail, username: signupUsername });
      if (res?.data?.success) {
        toast.success(res.data.message || "OTP sent successfully!");
      } else {
        toast.error(res?.data?.message || "Failed to resend OTP");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to resend OTP";
      toast.error(msg);
    }
  };

  if (showOtpScreen) {
    return (
      <OtpEnter
        emailId={signupEmail}
        setOtpMenuToggle={setShowOtpScreen}
        type="signup"
        onSubmit={handleOtpVerification}
        onSuccess={handleOtpSuccess}
        onResendOtp={handleResendOtp}
        isLogin={false}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white overflow-x-hidden">
      {/* Futuristic Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-conic from-yellow-500/10 via-transparent to-yellow-500/10 rounded-full blur-3xl animate-spin" style={{animationDuration: '20s'}}></div>
      </div>

      {/* No global header here; Layout hides header for /signup */}
      <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md mx-auto">
          <div className="bg-black/30 rounded-2xl p-8 backdrop-blur-sm border border-yellow-500/20 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-600/20 bg-black/20">
                <img src={logo} alt="Crezar Logo" className="w-12 h-12 object-contain" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
                Join Crezar
              </h1>
              <p className="text-gray-400">Create your account and start streaming</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Input */}
              <div className="space-y-2">
                <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                  Username
                </label>
                <div className="relative">
                  <User aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 transform text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                      errors.username ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                    }`}
                    placeholder="Choose a username"
                  />
                </div>
                {errors.username && <p className="text-red-400 text-sm">{errors.username}</p>}
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="emailId" className="block text-sm font-medium text-gray-300">
                  Email Address
                </label>
                <div className="relative">
                  <Mail aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 transform text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                  <input
                    id="emailId"
                    type="email"
                    name="emailId"
                    value={formData.emailId}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                      errors.emailId ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.emailId && <p className="text-red-400 text-sm">{errors.emailId}</p>}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <div className="relative">
                  <Lock aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 transform text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                      errors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                    }`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-400 text-sm">{errors.password}</p>}
              </div>

              {/* Confirm Password Input */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 transform text-yellow-400/80 w-5 h-5 z-10 pointer-events-none" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors backdrop-blur-sm ${
                      errors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-400 text-sm">{errors.confirmPassword}</p>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loader}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{loader ? "Creating Account..." : "Create Account"}</span>
                {!loader && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-gray-400 text-sm">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-yellow-400 hover:text-yellow-300 transition-colors font-semibold"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignupPage;
